import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Boxes, ExternalLink } from 'lucide-react';
import { useOrgStore } from '../store/useOrgStore';
import { StatCard } from '../components/ui/StatCard';
import { EmptyState } from '../components/ui/EmptyState';
import { RiskBadge } from '../components/ui/RiskBadge';
import { CollapsibleSection } from '../components/ui/CollapsibleSection';
import type { AppsResult, AppVendor } from '../types/org.types';

type Filter = 'all' | 'third-party' | 'salesforce';

function vendorBadge(vendor: AppVendor): { tone: 'critical' | 'medium' | 'neutral'; label: string } {
  if (vendor === 'third-party') return { tone: 'medium', label: 'Third-party' };
  if (vendor === 'salesforce') return { tone: 'neutral', label: 'Salesforce' };
  return { tone: 'neutral', label: 'Unknown' };
}

// Live audit store by default, which since TYR-216 is the client Hub alone;
// a stored client scan passes its own audit in.
// Passing `data` at all, null included, is scan mode: the store is not read.
export function AppsPage({ data }: { data?: AppsResult | null } = {}) {
  const storeApps = useOrgStore((s) => s.audit.apps);
  const scanMode = data !== undefined;
  const apps = scanMode ? data : storeApps;
  const [filter, setFilter] = useState<Filter>('third-party');

  const filteredPackages = useMemo(() => {
    if (!apps) return [];
    if (filter === 'all') return apps.installedPackages.rows;
    return apps.installedPackages.rows.filter((p) => p.vendor === filter);
  }, [apps, filter]);

  const filteredCAs = useMemo(() => {
    if (!apps) return [];
    if (filter === 'all') return apps.connectedApps.rows;
    return apps.connectedApps.rows.filter((c) => c.vendor === filter);
  }, [apps, filter]);

  if (!apps)
    return <EmptyState title="App data not loaded" hint="Reconnect to scan again." icon={<Boxes size={20} />} />;

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-design mb-1">Apps & Integrations</div>
        <h1 className="text-3xl font-semibold text-crux-light tracking-tight">Installed Apps</h1>
        <p className="text-sm text-neutral-500 mt-1 max-w-3xl">
          Every managed package and Connected App in the org. Third-party apps are highlighted because
          each one is a possible integration to retire, consolidate, or harden.
        </p>
      </div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
        className="grid grid-cols-2 sm:grid-cols-4 gap-3"
      >
        {[
          <StatCard
            key="pkg-tp"
            label="3rd-party packages"
            value={apps.installedPackages.thirdParty}
            hint={`of ${apps.installedPackages.total.toLocaleString()} installed`}
            accent="design"
          />,
          <StatCard
            key="pkg-sf"
            label="Salesforce packages"
            value={apps.installedPackages.salesforce}
            accent="adopt"
          />,
          <StatCard
            key="ca-tp"
            label="3rd-party Connected Apps"
            value={apps.connectedApps.thirdParty}
            hint={`of ${apps.connectedApps.total.toLocaleString()} total`}
            accent="design"
          />,
          <StatCard
            key="ca-sf"
            label="Salesforce Connected Apps"
            value={apps.connectedApps.salesforce}
            accent="adopt"
          />,
        ].map((c, i) => (
          <motion.div
            key={i}
            variants={{ hidden: { opacity: 0, y: 6 }, visible: { opacity: 1, y: 0, transition: { duration: 0.25 } } }}
          >
            {c}
          </motion.div>
        ))}
      </motion.div>

      <div className="flex items-center gap-1">
        {(['third-party', 'all', 'salesforce'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
              filter === f
                ? 'bg-design/15 border-design/40 text-design'
                : 'bg-surface-2 border-border text-neutral-400 hover:text-crux-light'
            }`}
          >
            {f === 'third-party' ? '3rd party' : f === 'all' ? 'All' : 'Salesforce'}
          </button>
        ))}
      </div>

      <CollapsibleSection
        title="Installed Packages"
        meta={`${filteredPackages.length} shown`}
      >
        {filteredPackages.length === 0 ? (
          <div className="text-sm text-neutral-500 py-6">No packages match this filter.</div>
        ) : (
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-2">
                <tr className="text-[11px] uppercase tracking-widest text-neutral-500">
                  <th className="text-left px-4 py-3 font-medium">Package</th>
                  <th className="text-left px-4 py-3 font-medium">Namespace</th>
                  <th className="text-left px-4 py-3 font-medium">Version</th>
                  <th className="text-left px-4 py-3 font-medium">Vendor</th>
                </tr>
              </thead>
              <tbody>
                {filteredPackages.map((p, i) => {
                  const v = vendorBadge(p.vendor);
                  return (
                    <tr key={p.id} className={i % 2 === 0 ? 'bg-surface-1/40' : 'bg-transparent'}>
                      <td className="px-4 py-2.5 text-crux-light">{p.name}</td>
                      <td className="px-4 py-2.5 text-neutral-400 font-mono text-xs">
                        {p.namespacePrefix ?? '-'}
                      </td>
                      <td className="px-4 py-2.5 text-neutral-400 tabular-nums">{p.version ?? '-'}</td>
                      <td className="px-4 py-2.5">
                        <RiskBadge tone={v.tone}>{v.label}</RiskBadge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        title="Connected Apps"
        meta={`${filteredCAs.length} shown`}
      >
        {filteredCAs.length === 0 ? (
          <div className="text-sm text-neutral-500 py-6">No Connected Apps match this filter.</div>
        ) : (
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-2">
                <tr className="text-[11px] uppercase tracking-widest text-neutral-500">
                  <th className="text-left px-4 py-3 font-medium">Connected App</th>
                  <th className="text-left px-4 py-3 font-medium">Admin-approved only</th>
                  <th className="text-left px-4 py-3 font-medium">Refresh-token policy</th>
                  <th className="text-left px-4 py-3 font-medium">Vendor</th>
                </tr>
              </thead>
              <tbody>
                {filteredCAs.map((c, i) => {
                  const v = vendorBadge(c.vendor);
                  return (
                    <tr key={c.id} className={i % 2 === 0 ? 'bg-surface-1/40' : 'bg-transparent'}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-crux-light">{c.name}</span>
                          {c.vendor === 'third-party' ? (
                            <ExternalLink size={12} className="text-neutral-600" />
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-neutral-400">
                        {c.optionsAdminApprovedOnly === null ? '-' : c.optionsAdminApprovedOnly ? 'Yes' : 'No'}
                      </td>
                      <td className="px-4 py-2.5 text-neutral-400">
                        {c.optionsRefreshTokenValidity === null
                          ? '-'
                          : c.optionsRefreshTokenValidity
                            ? 'App policy'
                            : 'User policy'}
                      </td>
                      <td className="px-4 py-2.5">
                        <RiskBadge tone={v.tone}>{v.label}</RiskBadge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CollapsibleSection>

      {apps.notes.length > 0 && (
        <div className="text-[11px] text-neutral-600 space-y-1">
          {apps.notes.map((n, i) => (
            <p key={i}>· {n}</p>
          ))}
        </div>
      )}
    </div>
  );
}
